import { create } from "zustand";
import { axiosInstance } from "../lib/axios";
import toast from "react-hot-toast";

export interface GatewayCred {
  publishableKey: string;
  secretKey: string;
  apiEndpoint: string;
  webhookUrl: string;
  webhookSecret: string;
  connectionStatus: "untested" | "success" | "failed";
  lastTestedAt: string | null;
}
export interface Gateway {
  provider: string;
  sandbox: GatewayCred;
  live: GatewayCred;
}
export interface PaymentMethod {
  key: string;
  label: string;
  enabled: boolean;
}
export interface NotificationRow {
  event: string;
  email: boolean;
  inApp: boolean;
  activityCenter: boolean;
}
export interface AuditEntry {
  at: string;
  userId?: string;
  userName: string;
  action: string;
}
export interface WebhookStatus {
  endpoint: string;
  totalEvents: number;
  lastEventAt: string | null;
  lastEventType: string;
  lastInvoice: string;
  lastResult: string;
  health: "healthy" | "stale" | "no-events";
}
export interface PaymentSettings {
  activeEnvironment: "sandbox" | "live";
  activeProvider: string;
  gateways: Gateway[];
  methods: PaymentMethod[];
  defaultMethod: string;
  bankDetails?: {
    accountName?: string; bankName?: string; accountNumber?: string;
    iban?: string; swift?: string; routingNumber?: string; instructions?: string;
  };
  defaultCurrency: string;
  currencyFormat: string;
  invoiceDuePeriodDays: number;
  invoicePrefix?: string;
  nextInvoiceNumber?: number;
  invoiceExpirationDays?: number;
  defaultTaxPercent?: number;
  defaultDiscountPercent?: number;
  defaultPaymentTerms?: string;
  autoGenerateReceipts: boolean;
  allowPartialPayments: boolean;
  autoMarkPaidOnZeroBalance: boolean;
  walletMaxBalance: number;
  walletDailyLimit: number;
  notifications: NotificationRow[];
  audit?: AuditEntry[];
  webhookStatus?: Record<string, WebhookStatus>;
}

export interface Transaction {
  _id: string;
  transactionId: string;
  gateway: string;
  environment: string;
  clientName: string;
  invoiceNumber: string;
  amount: number;
  currency: string;
  status: "Successful" | "Pending" | "Failed";
  processedOn: string;
}

interface TxState {
  transactions: Transaction[];
  total: number;
  page: number;
  limit: number;
}

interface State {
  settings: PaymentSettings | null;
  isLoading: boolean;
  isSaving: boolean;
  isTesting: boolean;
  tx: TxState;
  isLoadingTx: boolean;
  fetchSettings: () => Promise<void>;
  updateSettings: (payload: PaymentSettings) => Promise<void>;
  testConnection: (provider: string, environment: string) => Promise<void>;
  fetchTransactions: (params?: { environment?: string; status?: string; from?: string; to?: string; page?: number }) => Promise<void>;
}

export const usePaymentSettingsStore = create<State>((set, get) => ({
  settings: null,
  isLoading: false,
  isSaving: false,
  isTesting: false,
  tx: { transactions: [], total: 0, page: 1, limit: 10 },
  isLoadingTx: false,

  fetchSettings: async () => {
    set({ isLoading: true });
    try {
      const res = await axiosInstance.get("/payment-settings");
      set({ settings: res.data });
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to load payment settings");
    } finally {
      set({ isLoading: false });
    }
  },

  updateSettings: async (payload) => {
    set({ isSaving: true });
    try {
      const res = await axiosInstance.put("/payment-settings", payload);
      set({ settings: res.data });
      toast.success("Payment settings saved");
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to save settings");
    } finally {
      set({ isSaving: false });
    }
  },

  testConnection: async (provider, environment) => {
    set({ isTesting: true });
    try {
      const res = await axiosInstance.post("/payment-settings/test", { provider, environment });
      if (res.data.status === "success") toast.success(res.data.message || "Connection successful");
      else toast.error(res.data.message || "Connection failed");
      // Refresh so the status badge + last-tested reflect the result.
      await get().fetchSettings();
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to test connection");
    } finally {
      set({ isTesting: false });
    }
  },

  fetchTransactions: async (params = {}) => {
    set({ isLoadingTx: true });
    try {
      const res = await axiosInstance.get("/payment-settings/transactions", { params });
      set({ tx: { transactions: res.data.transactions, total: res.data.total, page: res.data.page, limit: res.data.limit } });
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to load transactions");
    } finally {
      set({ isLoadingTx: false });
    }
  },
}));
